'use client';

import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';

export interface SubGenreChipProps {
  label: string;
  count: number;
  isActive?: boolean;
  onSelect?: () => void;
  /** Tablist roving tabindex — only the active chip should be in the tab order. */
  tabIndex?: 0 | -1;
  /** Forwarded so the parent tablist can focus by index for arrow-key nav. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  /** Stable id for `aria-labelledby` on the matching tabpanel (WEB-549). */
  id?: string;
  /** Id of the controlled tabpanel — must resolve to a real element (WEB-549). */
  ariaControls?: string;
}

export function SubGenreChip({
  label,
  count,
  isActive = false,
  onSelect,
  tabIndex,
  buttonRef,
  id,
  ariaControls,
}: SubGenreChipProps): React.ReactElement {
  return (
    <button
      ref={buttonRef}
      type="button"
      role="tab"
      id={id}
      aria-selected={isActive}
      aria-controls={ariaControls}
      tabIndex={tabIndex}
      onClick={onSelect}
      className={cn(
        'inline-flex min-h-11 desktop:min-h-0 items-center gap-1.5 rounded-full border px-3 py-[5px] font-mono text-xs leading-none transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2',
        isActive
          ? 'border-clt-skyline bg-clt-skyline-wash text-clt-skyline-glow'
          : 'border-border bg-transparent text-muted-foreground hover:border-clt-skyline hover:text-clt-skyline-glow',
      )}
    >
      <span>{label}</span>
      <span className="font-semibold">{count}</span>
    </button>
  );
}
